/**
 * useVoiceRecognition Hook
 *
 * Encapsulates the Web Speech API for voice-to-text transcription.
 * Handles auto-restart on silence, duplicate prevention, and error states.
 * Audio is processed transiently by Google's servers (not stored).
 */

import { useState, useRef, useCallback, useEffect } from 'react';

// ============================================
// WEB SPEECH API TYPE DECLARATIONS
// ============================================

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognitionInstance;
    webkitSpeechRecognition: new () => SpeechRecognitionInstance;
  }
}

// ============================================
// TYPES
// ============================================

export interface UseVoiceRecognitionReturn {
  isSupported: boolean;
  isRecording: boolean;
  finalTranscript: string;
  interimTranscript: string;
  error: string | null;
  start: () => void;
  stop: () => void;
  reset: () => void;
}

// ============================================
// SPEECH RECOGNITION API DETECTION
// ============================================

const SpeechRecognitionAPI =
  typeof window !== 'undefined' ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

// ============================================
// HOOK
// ============================================

export function useVoiceRecognition(): UseVoiceRecognitionReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const stoppedByUserRef = useRef(false);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const gotResultRef = useRef(false);

  const isSupported = !!SpeechRecognitionAPI;

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = undefined;
    }
  }, []);

  const stop = useCallback(() => {
    stoppedByUserRef.current = true;
    clearSilenceTimer();
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsRecording(false);
    setInterimTranscript('');
  }, [clearSilenceTimer]);

  const start = useCallback(() => {
    if (!SpeechRecognitionAPI) return;

    setError(null);
    setInterimTranscript('');
    stoppedByUserRef.current = false;
    gotResultRef.current = false;

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || 'en-US';
    recognitionRef.current = recognition;

    recognition.onstart = () => {
      setIsRecording(true);
      // Warn if no results within 5s of starting
      silenceTimerRef.current = setTimeout(() => {
        if (!gotResultRef.current) {
          setError('No speech detected. Try speaking louder.');
        }
      }, 5000);
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      gotResultRef.current = true;
      clearSilenceTimer();

      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (!result?.[0]) continue;
        if (result.isFinal) {
          const text = result[0].transcript.trim();
          if (text) {
            setFinalTranscript((prev) => (prev ? prev + ' ' : '') + text);
          }
        } else {
          interim += result[0].transcript;
        }
      }
      setInterimTranscript(interim);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      clearSilenceTimer();
      switch (event.error) {
        case 'no-speech':
          setError('No speech detected');
          break;
        case 'audio-capture':
          setError('Microphone not found or disconnected');
          stop();
          break;
        case 'network':
          setError('Speech recognition requires internet');
          break;
        case 'not-allowed':
          setError('Microphone access denied');
          setIsRecording(false);
          break;
        default:
          setError(`Speech recognition error: ${event.error}`);
      }
    };

    // Auto-restart on end (Chrome fires onend after ~60s of silence)
    recognition.onend = () => {
      clearSilenceTimer();
      if (!stoppedByUserRef.current) {
        try {
          gotResultRef.current = false;
          recognition.start();
        } catch {
          setIsRecording(false);
        }
      } else {
        setIsRecording(false);
      }
    };

    try {
      recognition.start();
    } catch {
      setError('Failed to start speech recognition');
      setIsRecording(false);
    }
  }, [clearSilenceTimer, stop]);

  const reset = useCallback(() => {
    stop();
    setFinalTranscript('');
    setInterimTranscript('');
    setError(null);
  }, [stop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearSilenceTimer();
      if (recognitionRef.current) {
        stoppedByUserRef.current = true;
        recognitionRef.current.stop();
      }
    };
  }, [clearSilenceTimer]);

  return {
    isSupported,
    isRecording,
    finalTranscript,
    interimTranscript,
    error,
    start,
    stop,
    reset,
  };
}
